import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { useIsWebView } from "../hooks/useIsWebView";
import { Crown, X, ArrowRight, Loader2 } from "lucide-react";

/**
 * Lightweight banner shown on the dashboard when the user landed here after clicking
 * "Elegir Decide" / "Elegir Avanza" on the landing page. Prompts them to complete the
 * subscription via Stripe. One-click dismissal removes it for the rest of the session.
 * Automatically hidden for users who are already on that plan (or higher).
 */
const PLAN_WEIGHT = { descubre: 0, decide: 1, avanza: 2 };

const PlanIntentBanner = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isWebView = useIsWebView();
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("_ft_plan_intent");
      if (stored === "decide" || stored === "avanza") setIntent(stored);
    } catch (e) { /* private mode */ }
  }, []);

  // Auto-clear when the user has reached or surpassed the intended plan.
  useEffect(() => {
    if (!user || !intent) return;
    const current = PLAN_WEIGHT[user.plan || "descubre"] ?? 0;
    const wanted = PLAN_WEIGHT[intent] ?? 0;
    if (current >= wanted) {
      try { localStorage.removeItem("_ft_plan_intent"); } catch (e) { /* ignore */ }
      setIntent(null);
    }
  }, [user, intent]);

  if (!intent || !user || isWebView) return null;

  const priceLabel = intent === "decide" ? "5,99€" : "8,99€";
  const planLabel = intent === "decide" ? t("plan_decide") : t("plan_avanza");
  const color = intent === "decide" ? "#002FA7" : "#EC4899";

  const handleContinue = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/payments/create-checkout-session`, {
        plan: intent,
        cycle: "monthly",
        success_url: `${window.location.origin}/dashboard?subscription=success`,
        cancel_url: `${window.location.origin}/pricing`,
      });
      if (resp.data?.url) {
        try { localStorage.removeItem("_ft_plan_intent"); } catch (e) { /* ignore */ }
        window.location.href = resp.data.url;
        return;
      }
    } catch (e) {
      console.error("[PlanIntent] checkout failed:", e?.response?.data || e.message);
    }
    setLoading(false);
  };

  const handleDismiss = () => {
    try { localStorage.removeItem("_ft_plan_intent"); } catch (e) { /* ignore */ }
    setIntent(null);
  };

  return (
    <div
      className="relative z-10 border-b border-white/10"
      style={{ background: `linear-gradient(90deg, ${color}22, ${color}11)` }}
      data-testid="plan-intent-banner"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <Crown className="w-5 h-5 flex-shrink-0" style={{ color }} />
        <p className="text-white/90 text-xs sm:text-sm flex-1 leading-snug" data-testid="plan-intent-text">
          {t("plan_intent_banner").replace("{plan}", planLabel).replace("{price}", priceLabel)}
        </p>
        <button
          onClick={handleContinue}
          disabled={loading}
          className="text-xs font-semibold px-3 py-1.5 rounded-full text-white flex items-center gap-1 hover:opacity-90 transition disabled:opacity-50 flex-shrink-0"
          style={{ backgroundColor: color }}
          data-testid="plan-intent-continue-btn"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
            <>
              <span className="hidden sm:inline">{t("plan_intent_continue")}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </>
          )}
        </button>
        <button
          onClick={handleDismiss}
          className="text-white/50 hover:text-white/90 p-1 flex-shrink-0"
          data-testid="plan-intent-dismiss-btn"
          aria-label={t("plan_intent_dismiss")}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default PlanIntentBanner;
